import React, { forwardRef, type ReactNode } from 'react';

import { Box, type BoxProps, type XCSS, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
export type BaseCellProps = {
	/**
	 * A percentage of pixel width of the table to apply to a column.
	 */
	width?: string;
	/**
	 * Horizontal alignment of content.
	 */
	align?: keyof typeof alignMap;
	/**
	 * Display css property. Defaults to 'unset'.
	 */
	display?: 'unset' | 'block' | 'inline' | 'flex' | 'inline-flex' | 'inline-block' | 'table-cell';
	/**
	 * Whether the cell should render as a `td` or `th` element.
	 */
	as?: 'td' | 'th';
	/**
	 * Same behavior as the HTML attribute.
	 *
	 * @see 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/th#attr-scope'
	 */
	scope?: 'col' | 'row' | 'colgroup' | 'rowgroup';
	/**
	 * A `testId` prop is a unique string that appears as a data attribute `data-testid`
	 * in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * Content of the cell.
	 */
	children?: ReactNode;
	/**
	 * Number of columns to span.
	 */
	colSpan?: number;
	/**
	 * controlled styles to apply to the cell.
	 */
} & Pick<
	BoxProps<'div'>,
	| 'paddingBlock'
	| 'paddingInline'
	| 'backgroundColor'
	| 'xcss'
	| 'onMouseEnter'
	| 'onMouseLeave'
	| 'tabIndex'
	| 'onKeyDown'
>;

/**
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-sort
 */
export type SortDirection = 'ascending' | 'descending' | 'none' | 'other';

type InternalBaseCellProps = BaseCellProps & {
	sortDirection?: SortDirection;
};

const alignLeftStyles = xcss({
	textAlign: 'left',
});
const alignCenterStyles = xcss({
	textAlign: 'center',
});
const alignRightStyles = xcss({
	textAlign: 'right',
});

const focusOutline = xcss({
	':focus-visible': {
		//@ts-expect-error This is a hack to get better focus styles
		boxShadow: `0 0 0 2px ${token('color.border.focused')} inset`,
		outline: 'none',
	},
	':focus': {
		//@ts-expect-error This is a hack to get better focus styles
		boxShadow: `0 0 0 2px ${token('color.border.focused')} inset`,
	},
});

type alignMapOptions = 'text' | 'number' | 'icon';
const alignMap: { [k in alignMapOptions]: XCSS } = {
	text: alignLeftStyles,
	number: alignRightStyles,
	icon: alignCenterStyles,
} as const;

/**
 * __BaseCell__
 *
 * @internal
 *
 * Basic cell element.
 */
export const BaseCell = forwardRef<HTMLTableCellElement, InternalBaseCellProps>(
	(
		{
			testId,
			as,
			xcss: customXcss,
			children,
			align = 'text',
			display,
			paddingBlock = 'space.100',
			paddingInline = 'space.100',
			backgroundColor,
			scope,
			width,
			sortDirection,
			colSpan,
			onMouseEnter,
			onMouseLeave,
			tabIndex,
			onKeyDown,
		},
		ref,
	) => {
		return (
			<Box
				ref={ref}
				scope={scope}
				xcss={[
					alignMap[align],
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					xcss({ width }),
					tabIndex === 0 && focusOutline,
					customXcss as XCSS,
				]}
				backgroundColor={backgroundColor}
				paddingBlock={paddingBlock}
				paddingInline={paddingInline}
				as={as}
				//@ts-expect-error This is a hack to get around Box's typing not allowing 'unset' or 'table-cell'
				display={(display ?? 'unset') as unknown}
				testId={testId}
				aria-sort={sortDirection}
				colSpan={colSpan}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				tabIndex={tabIndex}
				onKeyDown={onKeyDown}
			>
				{children}
			</Box>
		);
	},
);
